import { AlertCircle, Inbox } from "lucide-react";
import DataTableCheckbox from "./DataTableCheckbox";
import { alignClass, cx, getCellValue } from "./utils";

const SKELETON_WIDTHS = ["w-3/4", "w-1/2", "w-2/3", "w-5/6", "w-2/5"];

// Clicks on these (inside a row) belong to the control, not to onRowClick.
export const INTERACTIVE =
  'a, button, input, select, textarea, label, [role="button"], [role="checkbox"], [role="link"], [role="menuitem"], [data-row-click-ignore]';

export const formatPlain = (value) => {
  if (value == null || value === "") return "—";
  if (typeof value === "boolean") return value ? "true" : "false";
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? "—" : value.toLocaleDateString();
  if (Array.isArray(value)) return value.join(", ");
  if (typeof value === "object") return "—"; // objects need a column.render
  return String(value);
};

function StateRow({ colSpan, children }) {
  return (
    <tr>
      <td colSpan={colSpan} className="px-4 py-14">
        {children}
      </td>
    </tr>
  );
}

export function DefaultState({ icon: Icon, message, tone = "muted", role }) {
  return (
    <div role={role} className="flex flex-col items-center justify-center gap-3 text-center">
      <span
        className={cx(
          "flex h-11 w-11 items-center justify-center rounded-full",
          tone === "error"
            ? "bg-red-50 text-red-600 dark:bg-red-500/10 dark:text-red-400"
            : "bg-app-table-header text-app-muted",
        )}
      >
        <Icon size={20} strokeWidth={1.75} aria-hidden="true" />
      </span>
      <p className={cx("text-sm font-medium", tone === "error" ? "text-app-error" : "text-app-secondary")}>
        {message}
      </p>
    </div>
  );
}

export default function DataTableBody({
  columns,
  rows,
  getRowKey,
  colSpan,
  loading,
  loadingRowCount,
  error,
  errorState,
  emptyState,
  selectable,
  selectedSet,
  isRowSelectable,
  onToggleRow,
  getRowAriaLabel,
  onRowClick,
  rowClassName,
  rowOffset,
  labels,
}) {
  if (loading) {
    return (
      <tbody>
        {Array.from({ length: loadingRowCount }, (_, r) => (
          <tr key={r} aria-hidden="true" className="border-b border-app-primary last:border-b-0">
            {selectable && (
              <td className="w-12 py-4 pl-4 pr-0">
                <div className="h-4 w-4 animate-pulse rounded bg-app-hover" />
              </td>
            )}
            {columns.map((column, c) => (
              <td key={column.key} className={cx("px-4 py-4", alignClass[column.align || "left"])}>
                <div
                  className={cx(
                    "inline-block h-3.5 max-w-full animate-pulse rounded-full bg-app-hover",
                    SKELETON_WIDTHS[(r + c) % SKELETON_WIDTHS.length],
                  )}
                  style={{ minWidth: 32 }}
                />
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    );
  }

  if (error) {
    return (
      <tbody>
        <StateRow colSpan={colSpan}>
          {errorState || (
            <DefaultState
              icon={AlertCircle}
              tone="error"
              role="alert"
              message={typeof error === "string" ? error : labels.error}
            />
          )}
        </StateRow>
      </tbody>
    );
  }

  if (rows.length === 0) {
    return (
      <tbody>
        <StateRow colSpan={colSpan}>
          {emptyState || <DefaultState icon={Inbox} message={labels.empty} />}
        </StateRow>
      </tbody>
    );
  }

  const handleRowClick = (event, row, index) => {
    // React bubbles events from portals (menus, popovers) through the row; ignore those.
    if (!event.currentTarget.contains(event.target)) return;
    const interactive = event.target.closest?.(INTERACTIVE);
    if (interactive && event.currentTarget.contains(interactive)) return;
    if (window.getSelection?.().toString()) return; // user is selecting text
    onRowClick(row, index);
  };

  const handleRowKeyDown = (event, row, index) => {
    if (event.target !== event.currentTarget) return;
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onRowClick(row, index);
    }
  };

  return (
    <tbody>
      {rows.map((row, i) => {
        const index = rowOffset + i;
        const key = getRowKey(row);
        const selected = selectable && selectedSet.has(key);
        const canSelect = !isRowSelectable || isRowSelectable(row);

        return (
          <tr
            key={key}
            data-state={selected ? "selected" : undefined}
            tabIndex={onRowClick ? 0 : undefined}
            onClick={onRowClick ? (e) => handleRowClick(e, row, index) : undefined}
            onKeyDown={onRowClick ? (e) => handleRowKeyDown(e, row, index) : undefined}
            className={cx(
              "border-b border-app-primary last:border-b-0 transition-colors duration-150",
              selected ? "bg-blue-50/60 dark:bg-blue-500/10" : "hover:bg-app-table-header",
              onRowClick &&
                "cursor-pointer focus:outline-none focus-visible:bg-blue-50/60 dark:focus-visible:bg-blue-500/10 focus-visible:shadow-[inset_3px_0_0_theme(colors.blue.600)]",
              typeof rowClassName === "function" ? rowClassName(row, index) : rowClassName,
            )}
          >
            {selectable && (
              <td className="w-12 py-3 pl-4 pr-0">
                <DataTableCheckbox
                  checked={Boolean(selected)}
                  disabled={!canSelect}
                  onChange={() => onToggleRow(key)}
                  aria-label={labels.selectRow(getRowAriaLabel ? getRowAriaLabel(row, index) : `row ${index + 1}`)}
                />
              </td>
            )}
            {columns.map((column) => {
              const value = getCellValue(row, column);
              return (
                <td
                  key={column.key}
                  className={cx(
                    "px-4 py-3 text-sm text-app-secondary",
                    alignClass[column.align || "left"],
                    column.wrap ? "whitespace-normal" : "whitespace-nowrap",
                    column.cellClassName,
                  )}
                >
                  {column.render ? column.render(value, row, index) : formatPlain(value)}
                </td>
              );
            })}
          </tr>
        );
      })}
    </tbody>
  );
}
